import { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart, BarChart, Bar, Cell, CartesianGrid, ScatterChart, Scatter, ZAxis } from "recharts";

// === MOCK DATA GENERATION ===
const generateChurnData = () => {
  const data = [];
  let churn = 22;
  for (let i = 0; i < 90; i++) {
    const date = new Date(2025, 10, 1);
    date.setDate(date.getDate() + i);
    churn += (Math.random() - 0.42) * 3;
    churn = Math.max(8, Math.min(48, churn));
    if (i === 45) churn -= 6;
    if (i > 45) churn += (Math.random() - 0.55) * 2;
    data.push({
      date: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      rawDate: date,
      churn: Math.round(churn * 10) / 10,
      day: i,
    });
  }
  return data;
};

const generateMergeTimeData = () => {
  const data = [];
  let mergeHours = 18;
  for (let i = 0; i < 12; i++) {
    const date = new Date(2025, 10, 1);
    date.setDate(date.getDate() + i * 7);
    mergeHours += (Math.random() - 0.45) * 8;
    mergeHours = Math.max(4, Math.min(96, mergeHours));
    data.push({
      week: `W${i + 1}`,
      hours: Math.round(mergeHours * 10) / 10,
    });
  }
  return data;
};

const generatePRSizeData = () => {
  const sizes = [];
  for (let i = 0; i < 60; i++) {
    const isAIDump = Math.random() > 0.7;
    const lines = isAIDump
      ? Math.floor(Math.random() * 800) + 300
      : Math.floor(Math.random() * 200) + 20;
    sizes.push({
      pr: i + 1,
      lines,
      reviews: Math.floor(Math.random() * 5) + 1,
      hoursToReview: Math.round((lines / 50 + Math.random() * 12) * 10) / 10,
    });
  }
  return sizes;
};

const generateGrowthData = () => {
  const data = [];
  let totalLines = 45000;
  for (let i = 0; i < 12; i++) {
    const date = new Date(2025, 10, 1);
    date.setDate(date.getDate() + i * 7);
    const added = Math.floor(Math.random() * 3000) + 1500;
    const deleted = Math.floor(Math.random() * 1200) + 400;
    totalLines += added - deleted;
    data.push({
      week: `W${i + 1}`,
      added,
      deleted: -deleted,
      net: added - deleted,
      total: totalLines,
    });
  }
  return data;
};

const hotspotFiles = [
  { file: "src/api/handlers/auth.ts", churnRate: 67, changes: 34, lines: 420 },
  { file: "src/components/Dashboard.tsx", churnRate: 54, changes: 28, lines: 680 },
  { file: "lib/utils/parser.ts", churnRate: 51, changes: 22, lines: 310 },
  { file: "src/api/handlers/billing.ts", churnRate: 44, changes: 19, lines: 520 },
  { file: "src/hooks/useData.ts", churnRate: 41, changes: 17, lines: 180 },
  { file: "config/webpack.config.js", churnRate: 38, changes: 15, lines: 290 },
  { file: "src/services/notifications.ts", churnRate: 35, changes: 14, lines: 440 },
  { file: "src/api/middleware/cors.ts", churnRate: 31, changes: 11, lines: 95 },
];

const events = [
  { day: 12, label: "Cursor agent mode enabled", type: "config" },
  { day: 30, label: "New engineer onboarded", type: "team" },
  { day: 45, label: "Added .cursorrules file", type: "fix" },
  { day: 62, label: "CodeRabbit added to CI", type: "config" },
  { day: 78, label: "Review gate enforced", type: "fix" },
];

// === COMPONENTS ===

const StatusBadge = ({ status }) => {
  const colors = {
    red: { bg: "#FEF2F2", border: "#FECACA", text: "#DC2626", label: "Critical" },
    yellow: { bg: "#FFFBEB", border: "#FDE68A", text: "#D97706", label: "Warning" },
    green: { bg: "#F0FDF4", border: "#BBF7D0", text: "#16A34A", label: "Healthy" },
  };
  const c = colors[status];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 12px", borderRadius: 4,
      background: c.bg, border: `1px solid ${c.border}`, color: c.text,
      fontSize: 12, fontWeight: 600, letterSpacing: 0.5,
      fontFamily: "'JetBrains Mono', monospace",
    }}>
      <span style={{
        width: 6, height: 6, borderRadius: "50%",
        background: c.text,
      }} />
      {c.label}
    </span>
  );
};

const MetricCard = ({ label, value, unit, status, subtext }) => (
  <div style={{
    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
    padding: "20px 24px", flex: "1 1 200px", minWidth: 200,
    transition: "border-color 0.2s",
  }}>
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      marginBottom: 12,
    }}>
      <span style={{
        color: "#6b7280", fontSize: 11, textTransform: "uppercase",
        letterSpacing: 1.2, fontFamily: "'JetBrains Mono', monospace",
      }}>{label}</span>
      <StatusBadge status={status} />
    </div>
    <div style={{
      fontSize: 36, fontWeight: 700, color: "#1a1a1a",
      fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1,
      marginBottom: 6,
    }}>
      {value}<span style={{ fontSize: 16, color: "#9ca3af", marginLeft: 4 }}>{unit}</span>
    </div>
    <div style={{
      fontSize: 12, color: "#9ca3af", fontFamily: "'JetBrains Mono', monospace",
    }}>{subtext}</div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6,
      padding: "10px 14px", fontFamily: "'JetBrains Mono', monospace",
      fontSize: 12, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
    }}>
      <div style={{ color: "#6b7280", marginBottom: 4 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || "#1a1a1a" }}>
          {p.name}: {p.value}{typeof p.value === "number" && p.name?.includes("hour") ? "h" : p.unit || ""}
        </div>
      ))}
    </div>
  );
};

const SectionHeader = ({ title, description }) => (
  <div style={{ marginBottom: 20, marginTop: 48 }}>
    <h2 style={{
      fontSize: 18, fontWeight: 600, color: "#1a1a1a",
      fontFamily: "'Space Grotesk', sans-serif",
      marginBottom: 4, letterSpacing: -0.3,
    }}>{title}</h2>
    <p style={{
      fontSize: 13, color: "#6b7280",
      fontFamily: "'JetBrains Mono', monospace",
    }}>{description}</p>
  </div>
);

// === MAIN DASHBOARD ===

export default function Scorecard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [teamSize, setTeamSize] = useState(22);
  const [avgComp, setAvgComp] = useState(165000);

  const churnData = useMemo(() => generateChurnData(), []);
  const mergeData = useMemo(() => generateMergeTimeData(), []);
  const prSizeData = useMemo(() => generatePRSizeData(), []);
  const growthData = useMemo(() => generateGrowthData(), []);

  const currentChurn = churnData[churnData.length - 1].churn;
  const avgMergeTime = Math.round(mergeData.reduce((a, b) => a + b.hours, 0) / mergeData.length * 10) / 10;
  const medianPRSize = [...prSizeData].sort((a, b) => a.lines - b.lines)[Math.floor(prSizeData.length / 2)].lines;
  const avgReviewCycles = Math.round(prSizeData.reduce((a, b) => a + b.reviews, 0) / prSizeData.length * 10) / 10;
  const hotspotConcentration = 62;

  // Dollar calculations
  const hourlyRate = avgComp / 2080;
  const monthlyWasteHours = teamSize * (currentChurn / 100) * 20;
  const monthlyWaste = Math.round(monthlyWasteHours * hourlyRate);
  const annualWaste = monthlyWaste * 12;
  const targetChurn = 15;
  const potentialSavings = Math.round((monthlyWaste * (1 - targetChurn / currentChurn)));

  const churnStatus = currentChurn > 30 ? "red" : currentChurn > 15 ? "yellow" : "green";
  const mergeStatus = avgMergeTime > 72 ? "red" : avgMergeTime > 24 ? "yellow" : "green";
  const prSizeStatus = medianPRSize > 500 ? "red" : medianPRSize > 200 ? "yellow" : "green";
  const reviewStatus = avgReviewCycles > 3 ? "red" : avgReviewCycles > 2 ? "yellow" : "green";

  return (
    <div style={{
      background: "#fafafa", minHeight: "100vh", color: "#1a1a1a",
      fontFamily: "'Space Grotesk', sans-serif",
      padding: "0 20px 60px",
      maxWidth: 960, margin: "0 auto",
    }}>
      <link href="https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap" rel="stylesheet" />

      {/* Header */}
      <div style={{ padding: "40px 0 32px", borderBottom: "1px solid #e5e7eb", marginBottom: 32 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 6,
              background: "linear-gradient(135deg, #FF6B35 0%, #FF3D00 100%)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 16, fontWeight: 700, color: "#fff",
            }}>B</div>
            <span style={{
              fontSize: 14, fontWeight: 600, letterSpacing: 2,
              textTransform: "uppercase", color: "#6b7280",
              fontFamily: "'JetBrains Mono', monospace",
            }}>Bonzai</span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => navigate(`/data?${searchParams.toString()}`)}
              style={{
                background: "linear-gradient(135deg, #FF6B35 0%, #FF3D00 100%)",
                border: "none",
                borderRadius: 6,
                padding: "8px 16px",
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              View Data
            </button>
            <button
              onClick={() => navigate('/connect')}
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 6,
                padding: "8px 16px",
                color: "#6b7280",
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "'JetBrains Mono', monospace",
              }}
            >
              Back
            </button>
          </div>
        </div>

        <h1 style={{
          fontSize: 32, fontWeight: 700, letterSpacing: -1,
          marginBottom: 8, lineHeight: 1.1, color: "#1a1a1a",
        }}>
          Workflow Scorecard
        </h1>
        <p style={{
          fontSize: 14, color: "#6b7280",
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          acme/web-platform · {teamSize} engineers · Generated Feb 11, 2026
        </p>
      </div>

      {/* Team Inputs */}
      <div style={{
        display: "flex", gap: 24, marginBottom: 32,
        padding: "20px 24px",
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 8,
      }}>
        <div style={{ flex: 1 }}>
          <label style={{
            display: "block",
            fontSize: 11,
            color: "#6b7280",
            textTransform: "uppercase",
            letterSpacing: 1,
            marginBottom: 8,
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            Team Size
          </label>
          <input
            type="number"
            value={teamSize}
            onChange={(e) => setTeamSize(Math.max(1, parseInt(e.target.value) || 1))}
            style={{
              width: "100%",
              padding: "12px 16px",
              fontSize: 18,
              fontWeight: 600,
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              fontFamily: "'Space Grotesk', sans-serif",
              color: "#1a1a1a",
            }}
          />
          <span style={{
            fontSize: 12,
            color: "#9ca3af",
            fontFamily: "'JetBrains Mono', monospace",
          }}>engineers</span>
        </div>
        <div style={{ flex: 1 }}>
          <label style={{
            display: "block",
            fontSize: 11,
            color: "#6b7280",
            textTransform: "uppercase",
            letterSpacing: 1,
            marginBottom: 8,
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            Avg. Compensation
          </label>
          <div style={{ position: "relative" }}>
            <span style={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: 18,
              fontWeight: 600,
              color: "#9ca3af",
            }}>$</span>
            <input
              type="number"
              value={avgComp}
              onChange={(e) => setAvgComp(Math.max(0, parseInt(e.target.value) || 0))}
              style={{
                width: "100%",
                padding: "12px 16px 12px 32px",
                fontSize: 18,
                fontWeight: 600,
                border: "1px solid #e5e7eb",
                borderRadius: 6,
                fontFamily: "'Space Grotesk', sans-serif",
                color: "#1a1a1a",
              }}
            />
          </div>
          <span style={{
            fontSize: 12,
            color: "#9ca3af",
            fontFamily: "'JetBrains Mono', monospace",
          }}>per year (fully loaded)</span>
        </div>
      </div>

      {/* Cost headline */}
      <div style={{
        background: "linear-gradient(135deg, #FEF2F2 0%, #fff 100%)",
        border: "1px solid #FECACA",
        borderRadius: 12, padding: "32px 36px", marginBottom: 36,
        position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", top: 0, right: 0, width: 200, height: 200,
          background: "radial-gradient(circle, rgba(220,38,38,0.05) 0%, transparent 70%)",
        }} />
        <div style={{
          fontSize: 11, color: "#DC2626", textTransform: "uppercase",
          letterSpacing: 1.5, marginBottom: 8,
          fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
        }}>
          Estimated monthly waste from code rework
        </div>
        <div style={{
          fontSize: 56, fontWeight: 700, color: "#DC2626",
          letterSpacing: -2, lineHeight: 1, marginBottom: 8,
        }}>
          ${monthlyWaste.toLocaleString()}
          <span style={{ fontSize: 18, color: "#F87171", marginLeft: 4 }}>/mo</span>
        </div>
        <div style={{
          fontSize: 13, color: "#6b7280",
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          {Math.round(monthlyWasteHours)} engineer-hours/mo in rework · ${annualWaste.toLocaleString()}/yr · Potential savings: <span style={{ color: "#16A34A" }}>${potentialSavings.toLocaleString()}/mo</span>
        </div>
      </div>

      {/* Metric cards */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12,
      }}>
        <MetricCard
          label="Code Churn Rate" value={currentChurn} unit="%"
          status={churnStatus}
          subtext="Industry avg ~35% · Healthy <15%"
        />
        <MetricCard
          label="Median PR Merge Time" value={avgMergeTime} unit="hrs"
          status={mergeStatus}
          subtext="Target <24hrs"
        />
        <MetricCard
          label="Median PR Size" value={medianPRSize} unit="lines"
          status={prSizeStatus}
          subtext="Target <200 lines"
        />
      </div>
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 0,
      }}>
        <MetricCard
          label="Avg Review Cycles" value={avgReviewCycles} unit="rounds"
          status={reviewStatus}
          subtext="Target 1-2 rounds"
        />
        <MetricCard
          label="Hotspot Concentration" value={hotspotConcentration} unit="%"
          status="red"
          subtext="Top 10% files = 62% of all churn"
        />
        <MetricCard
          label="Time to First Review" value="6.2" unit="hrs"
          status="yellow"
          subtext="Target <4hrs"
        />
      </div>

      {/* Churn over time */}
      <SectionHeader
        title="Code Churn Rate - 90 Day Trend"
        description="% of code rewritten or deleted within 14 days of being written"
      />
      <div style={{
        background: "#fff", border: "1px solid #e5e7eb",
        borderRadius: 8, padding: "24px 20px 16px",
      }}>
        {/* Event legend */}
        <div style={{
          display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap",
        }}>
          {events.map((e, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 6,
              fontSize: 11, color: "#6b7280",
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              <span style={{
                width: 8, height: 8, borderRadius: "50%",
                background: e.type === "fix" ? "#16A34A" : e.type === "config" ? "#D97706" : "#3B82F6",
              }} />
              {e.label}
            </div>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={churnData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id="churnGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#FF6B35" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#FF6B35" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="date" tick={{ fill: "#9ca3af", fontSize: 10, fontFamily: "JetBrains Mono" }}
              tickLine={false} axisLine={{ stroke: "#e5e7eb" }}
              interval={14}
            />
            <YAxis
              tick={{ fill: "#9ca3af", fontSize: 10, fontFamily: "JetBrains Mono" }}
              tickLine={false} axisLine={{ stroke: "#e5e7eb" }}
              domain={[0, 50]} unit="%"
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={15} stroke="#16A34A" strokeDasharray="6 4" strokeOpacity={0.6} label={{ value: "Healthy", fill: "#16A34A", fontSize: 10, fontFamily: "JetBrains Mono" }} />
            <ReferenceLine y={30} stroke="#DC2626" strokeDasharray="6 4" strokeOpacity={0.6} label={{ value: "Critical", fill: "#DC2626", fontSize: 10, fontFamily: "JetBrains Mono" }} />
            <Area
              type="monotone" dataKey="churn" stroke="#FF6B35"
              strokeWidth={2} fill="url(#churnGrad)" dot={false}
              name="Churn %"
            />
            {events.map((event, i) => (
              <ReferenceLine
                key={i} x={churnData[event.day]?.date}
                stroke={event.type === "fix" ? "#16A34A" : event.type === "config" ? "#D97706" : "#3B82F6"}
                strokeDasharray="4 4" strokeOpacity={0.5}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* PR Merge Time */}
      <SectionHeader
        title="PR Merge Time - Weekly"
        description="Median hours from PR open to merge, by week"
      />
      <div style={{
        background: "#fff", border: "1px solid #e5e7eb",
        borderRadius: 8, padding: "24px 20px 16px",
      }}>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={mergeData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="week" tick={{ fill: "#9ca3af", fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#e5e7eb" }} />
            <YAxis tick={{ fill: "#9ca3af", fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#e5e7eb" }} unit="h" />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={24} stroke="#16A34A" strokeDasharray="6 4" strokeOpacity={0.5} />
            <Bar dataKey="hours" name="Merge time" radius={[3, 3, 0, 0]}>
              {mergeData.map((entry, i) => (
                <Cell key={i} fill={entry.hours > 72 ? "#EF4444" : entry.hours > 24 ? "#F59E0B" : "#22C55E"} opacity={0.85} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* PR Size vs Review Time scatter */}
      <SectionHeader
        title="PR Size vs. Review Time"
        description="Larger PRs take exponentially longer to review - look for the AI dump cluster"
      />
      <div style={{
        background: "#fff", border: "1px solid #e5e7eb",
        borderRadius: 8, padding: "24px 20px 16px",
      }}>
        <ResponsiveContainer width="100%" height={240}>
          <ScatterChart margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey="lines" name="PR Size" unit=" lines"
              tick={{ fill: "#9ca3af", fontSize: 10, fontFamily: "JetBrains Mono" }}
              tickLine={false} axisLine={{ stroke: "#e5e7eb" }}
            />
            <YAxis
              dataKey="hoursToReview" name="Review Time" unit="h"
              tick={{ fill: "#9ca3af", fontSize: 10, fontFamily: "JetBrains Mono" }}
              tickLine={false} axisLine={{ stroke: "#e5e7eb" }}
            />
            <ZAxis dataKey="reviews" range={[30, 150]} name="Review Cycles" />
            <Tooltip content={<CustomTooltip />} />
            <Scatter data={prSizeData} name="PRs">
              {prSizeData.map((entry, i) => (
                <Cell key={i} fill={entry.lines > 500 ? "#EF4444" : entry.lines > 200 ? "#F59E0B" : "#22C55E"} opacity={0.7} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
        <div style={{
          display: "flex", gap: 20, marginTop: 12, justifyContent: "center",
        }}>
          {[
            { color: "#22C55E", label: "<200 lines (healthy)" },
            { color: "#F59E0B", label: "200-500 lines (large)" },
            { color: "#EF4444", label: ">500 lines (AI dump)" },
          ].map((item, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 6,
              fontSize: 11, color: "#6b7280", fontFamily: "'JetBrains Mono', monospace",
            }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: item.color }} />
              {item.label}
            </div>
          ))}
        </div>
      </div>

      {/* Codebase Growth */}
      <SectionHeader
        title="Codebase Growth"
        description="Weekly lines added vs deleted - steep growth with high churn means bloat"
      />
      <div style={{
        background: "#fff", border: "1px solid #e5e7eb",
        borderRadius: 8, padding: "24px 20px 16px",
      }}>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={growthData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }} stackOffset="sign">
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="week" tick={{ fill: "#9ca3af", fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#e5e7eb" }} />
            <YAxis tick={{ fill: "#9ca3af", fontSize: 10, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#e5e7eb" }} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="added" name="Lines added" fill="#22C55E" opacity={0.75} stackId="stack" radius={[3, 3, 0, 0]} />
            <Bar dataKey="deleted" name="Lines deleted" fill="#EF4444" opacity={0.75} stackId="stack" radius={[0, 0, 3, 3]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Hotspot Files */}
      <SectionHeader
        title="Hotspot Files"
        description="Files with the highest churn - where tech debt is concentrating"
      />
      <div style={{
        background: "#fff", border: "1px solid #e5e7eb",
        borderRadius: 8, overflow: "hidden",
      }}>
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 100px 80px 80px",
          padding: "12px 20px",
          borderBottom: "1px solid #e5e7eb",
          fontSize: 10, color: "#6b7280", textTransform: "uppercase",
          letterSpacing: 1, fontFamily: "'JetBrains Mono', monospace",
          background: "#f9fafb",
        }}>
          <span>File</span>
          <span style={{ textAlign: "right" }}>Churn Rate</span>
          <span style={{ textAlign: "right" }}>Changes</span>
          <span style={{ textAlign: "right" }}>Lines</span>
        </div>
        {hotspotFiles.map((file, i) => (
          <div key={i} style={{
            display: "grid",
            gridTemplateColumns: "1fr 100px 80px 80px",
            padding: "14px 20px",
            borderBottom: i < hotspotFiles.length - 1 ? "1px solid #f3f4f6" : "none",
            fontSize: 13, fontFamily: "'JetBrains Mono', monospace",
            position: "relative",
            transition: "background 0.15s",
          }}>
            <div style={{
              position: "absolute", left: 0, top: 0, bottom: 0,
              width: `${file.churnRate}%`,
              background: file.churnRate > 50
                ? "linear-gradient(90deg, rgba(239,68,68,0.08) 0%, transparent 100%)"
                : file.churnRate > 35
                ? "linear-gradient(90deg, rgba(245,158,11,0.06) 0%, transparent 100%)"
                : "transparent",
            }} />
            <span style={{ color: "#374151", position: "relative", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {file.file}
            </span>
            <span style={{
              textAlign: "right", position: "relative",
              color: file.churnRate > 50 ? "#DC2626" : file.churnRate > 35 ? "#D97706" : "#16A34A",
              fontWeight: 600,
            }}>
              {file.churnRate}%
            </span>
            <span style={{ textAlign: "right", color: "#9ca3af", position: "relative" }}>
              {file.changes}
            </span>
            <span style={{ textAlign: "right", color: "#9ca3af", position: "relative" }}>
              {file.lines}
            </span>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div style={{
        marginTop: 56, textAlign: "center",
        padding: "48px 36px",
        background: "#fff",
        border: "1px solid #e5e7eb", borderRadius: 12,
      }}>
        <div style={{
          fontSize: 28, fontWeight: 700, letterSpacing: -0.5,
          marginBottom: 12, color: "#1a1a1a",
        }}>
          Your workflow is costing you <span style={{ color: "#DC2626" }}>${annualWaste.toLocaleString()}/yr</span>
        </div>
        <p style={{
          fontSize: 14, color: "#6b7280", maxWidth: 500, margin: "0 auto 28px",
          fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.6,
        }}>
          This audit is free, forever. Our bread and butter is fixing it. We'll get your churn under 15% and prove it with a re-run.
        </p>
        <button style={{
          background: "linear-gradient(135deg, #FF6B35 0%, #FF3D00 100%)",
          color: "#fff", border: "none", borderRadius: 8,
          padding: "16px 48px", fontSize: 16, fontWeight: 600,
          cursor: "pointer", fontFamily: "'Space Grotesk', sans-serif",
          letterSpacing: 0.3,
          boxShadow: "0 4px 14px rgba(255,60,0,0.25)",
          transition: "transform 0.15s, box-shadow 0.15s",
        }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-1px)";
            e.target.style.boxShadow = "0 6px 20px rgba(255,60,0,0.3)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 14px rgba(255,60,0,0.25)";
          }}
        >
          Book a Fix Call
        </button>
        <div style={{
          marginTop: 16, fontSize: 12, color: "#9ca3af",
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          Typical engagement: 2-4 weeks · Pay only if your metrics improve
        </div>
      </div>

      {/* Footer */}
      <div style={{
        marginTop: 40, padding: "20px 0",
        borderTop: "1px solid #e5e7eb",
        display: "flex", justifyContent: "space-between",
        fontSize: 11, color: "#9ca3af",
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        <span>bonzai.dev · workflow observability</span>
        <span>Data sourced from GitHub API · No source code accessed</span>
      </div>
    </div>
  );
}
